import { clamp } from './curve'
import { G, SIDES, type GaitResult, type Side } from './gait'
import {
  isRunning,
  segmentMass,
  segmentSpecs,
  stanceWeight,
  totalMass,
  type GroundResult,
  type SegmentSpec,
} from './ground'
import { nodeFrame, nodePoint, type NodeKey, type Pose, type Skeleton } from './kinematics'
import { segmentLength, type HumanoidModel, type SegmentKey } from './model'
import { inPhase } from './moves'
import { stanceFraction } from './reference'
import { carrying, type SimState } from './state'
import { lerpVec } from './vec'
import {
  add,
  angularVelocity,
  apply,
  cross,
  dot,
  length,
  scale,
  sub,
  transpose,
  vec,
  type Mat3,
  type Vec3,
} from './vec'

/**
 * Inverse Dynamik — von der Bewegung zum Moment.
 *
 * Die Kette läuft vom Boden aufwärts: die Bodenreaktionskraft greift am
 * Druckmittelpunkt an, und Glied für Glied wird gefragt, welche Kraft und
 * welches Moment am proximalen Gelenk anliegen müssen, damit das Glied sich so
 * bewegt, wie es sich bewegt. Newton-Euler, nichts Exotisches.
 *
 * DREI STELLEN, AN DENEN ES SCHIEFGEHT UND NIEMAND ES SIEHT:
 *
 * 1. Die Schwerpunktbahn muss geglättet werden. Das starre Kontaktmodell
 *    erzeugt am Fersenaufsatz einen Knick, den ein wirklicher Körper über
 *    Fussrolle und Kniedämpfung abfängt. Ungefiltert explodiert die
 *    Bodenreaktionskraft — und mit ihr jedes Moment.
 *
 * 2. Die Last muss zwischen den Füssen so verteilt werden, dass die Summe immer
 *    die volle Schwerpunktkraft ergibt. Sonst geht an den Rändern der
 *    Standphase Kraft verloren, die dem Impuls fehlt und als Blindarbeit in den
 *    Gelenken wieder auftaucht.
 *
 * 3. Der Druckmittelpunkt kommt aus dem Momentengleichgewicht (ZMP), nicht aus
 *    einem Zeitschema. Ein vorgegebener Abrollverlauf sieht plausibel aus und
 *    ist bei jeder Beschleunigung falsch.
 */

/* -------------------------------------------------------------------------
   Bahnen — GEFILTERT, bevor irgendetwas abgeleitet wird
   ------------------------------------------------------------------------- */

/**
 * Filterdurchgänge der Bahnen.
 *
 * DIE WICHTIGSTEN DREI ZAHLEN DIESER DATEI.
 *
 * Zweimal numerisch ableiten verstärkt jede Unstetigkeit um Grössenordnungen.
 * Ohne diese Filterung liefert der Fersenaufsatz Winkelbeschleunigungen von
 * mehreren hundert Rad je Sekundenquadrat, die Bodenreaktionskraft explodiert,
 * und die Momente, aus denen anschliessend Motoren ausgelegt werden, sind
 * Rauschen mit einer plausibel aussehenden Kurvenform.
 *
 * Die Zahlen stammen aus der Vorlage und sind dort begründet: Punktbahnen auf
 * rund 8 Hz, Orientierungen etwas weniger, die Schwerpunktbahn zusätzlich auf
 * rund 3 Hz — sie trägt die Bodenreaktionskraft und ist die empfindlichste
 * Grösse der ganzen Kette.
 */
export const FILTER = { point: 26, frame: 18, com: 14 } as const

/** Zyklische [1,2,1]-Glättung einer Punktfolge. */
function smoothPath(path: Vec3[], passes: number): void {
  const n = path.length
  const bx = new Float64Array(n)
  const by = new Float64Array(n)
  const bz = new Float64Array(n)
  for (let p = 0; p < passes; p++) {
    for (let i = 0; i < n; i++) {
      bx[i] = path[i].x
      by[i] = path[i].y
      bz[i] = path[i].z
    }
    for (let i = 0; i < n; i++) {
      const a = (i - 1 + n) % n
      const c = (i + 1) % n
      path[i] = {
        x: 0.25 * bx[a] + 0.5 * bx[i] + 0.25 * bx[c],
        y: 0.25 * by[a] + 0.5 * by[i] + 0.25 * by[c],
        z: 0.25 * bz[a] + 0.5 * bz[i] + 0.25 * bz[c],
      }
    }
  }
}

/**
 * Zyklische Glättung einer Folge von Drehmatrizen.
 *
 * Gemittelt wird komponentenweise; das Ergebnis ist keine Drehmatrix mehr und
 * wird deshalb anschliessend wieder orthonormiert. Ohne die Orthonormierung
 * bekäme der Trägheitstensor eine Skalierung mit — die Momente wären dann um
 * einen Faktor daneben, den niemand bemerkt, weil die Kurvenform stimmt.
 */
function smoothFrames(frames: Mat3[], passes: number): void {
  const n = frames.length
  const buffer: Mat3[] = new Array(n)
  for (let p = 0; p < passes; p++) {
    for (let i = 0; i < n; i++) buffer[i] = frames[i]
    for (let i = 0; i < n; i++) {
      const a = buffer[(i - 1 + n) % n]
      const b = buffer[i]
      const c = buffer[(i + 1) % n]
      const out = new Float64Array(9)
      for (let k = 0; k < 9; k++) out[k] = 0.25 * a[k] + 0.5 * b[k] + 0.25 * c[k]
      frames[i] = out
    }
  }
  for (let i = 0; i < n; i++) frames[i] = orthonormalise(frames[i])
}

/** Gram-Schmidt über die Spalten — macht aus einer fast-Drehmatrix eine echte. */
export function orthonormalise(m: Mat3): Mat3 {
  const col = (k: number): Vec3 => vec(m[k * 3], m[k * 3 + 1], m[k * 3 + 2])
  const unit = (v: Vec3): Vec3 => {
    const l = length(v)
    return l > 1e-12 ? scale(v, 1 / l) : vec(1, 0, 0)
  }
  const x = unit(col(0))
  const y = unit(sub(col(1), scale(x, dot(x, col(1)))))
  const z = cross(x, y)
  return Float64Array.from([x.x, x.y, x.z, y.x, y.y, y.z, z.x, z.y, z.z])
}

/** Zweite Ableitung einer zyklischen Punktfolge. */
function secondDerivative(path: Vec3[], dt: number): Vec3[] {
  const n = path.length
  const out: Vec3[] = []
  for (let i = 0; i < n; i++) {
    const a = path[(i - 1 + n) % n]
    const b = path[i]
    const c = path[(i + 1) % n]
    out.push({
      x: (a.x + c.x - 2 * b.x) / (dt * dt),
      y: (a.y + c.y - 2 * b.y) / (dt * dt),
      z: (a.z + c.z - 2 * b.z) / (dt * dt),
    })
  }
  return out
}

/** Zentrale Differenz einer zyklischen Punktfolge. */
function firstDerivative(path: Vec3[], dt: number): Vec3[] {
  const n = path.length
  const out: Vec3[] = []
  for (let i = 0; i < n; i++) {
    out.push(scale(sub(path[(i + 1) % n], path[(i - 1 + n) % n]), 1 / (2 * dt)))
  }
  return out
}

/** Schlüssel einer Bahn: Punktname plus Seite. */
const trackKey = (name: string, side: Side | null): string => (side ? `${name}${side}` : name)

/* -------------------------------------------------------------------------
   Segmente
   ------------------------------------------------------------------------- */

/** Zeitverlauf eines Segments. */
export interface SegmentTrack {
  key: SegmentKey
  side: Side | null
  name: string
  /** Masse in Kilogramm. */
  mass: number
  /** Bezugslänge des Trägheitsradius, m. */
  length: number
  /** Schwerpunktbahn. */
  centre: Vec3[]
  /** Schwerpunktbeschleunigung. */
  acceleration: Vec3[]
  /** Winkelgeschwindigkeit. */
  omega: Vec3[]
  /** Winkelbeschleunigung. */
  alpha: Vec3[]
  /** Orientierung. */
  frame: Mat3[]
}

/* -------------------------------------------------------------------------
   Der ganze Durchlauf
   ------------------------------------------------------------------------- */

export interface DynamicsResult {
  samples: number
  cycleTime: number
  skeleton: Skeleton
  poses: Pose[]
  segments: SegmentTrack[]
  /** Gefilterte Bahnen aller benannten Punkte — die Grundlage jeder Ableitung. */
  tracks: Record<string, Vec3[]>
  /** Gesamtmasse einschliesslich Zuladung, kg. */
  mass: number
  /** Schwerpunktbahn des ganzen Körpers, geglättet. */
  com: Vec3[]
  /** Schwerpunktbeschleunigung. */
  comAcceleration: Vec3[]
  /** Momentanes Kraftangriffszentrum am Boden (ZMP). */
  zmp: Vec3[]
  /** Bodenreaktionskraft je Fuss, N. */
  grf: Record<Side, Vec3[]>
  /** Druckmittelpunkt je Fuss. */
  cop: Record<Side, Vec3[]>
  /** Lastanteil je Fuss, 0 … 1. */
  load: Record<Side, number[]>
  /** Gelenkmoment im Weltsystem, Nm. */
  moments: Record<string, Vec3[]>
  /** Gemessener Standphasenanteil in Prozent. */
  stancePercent: number
}

const jointKey = (joint: string, side: Side): string => `${joint}${side}`

/**
 * Wie weit der Druckmittelpunkt in den Vorfuss läuft — Anteil der Strecke
 * Ballen → Zehenspitze. Bis zur Spitze ist keine tragende Fläche mehr.
 */
const FOREFOOT = 0.55

/** Halbe Sohlenbreite, m — seitliche Grenze des Druckmittelpunkts. */
const HALF_FOOT_WIDTH = 0.035

/** Glättungsdurchgänge des Druckmittelpunkts: er springt an den Rändern der Stützfläche. */
const COP_SMOOTHING = 8

/**
 * Die fest vorgegebene Abrollkurve, wenn der Druckmittelpunkt NICHT aus dem
 * Momentengleichgewicht kommt: `u = 0,03 + 0,97 · q^0,72` über die Standphase.
 *
 * Der Exponent unter eins lässt den Punkt am Anfang schnell und am Ende langsam
 * wandern — die Ferse löst sich früh, der Vorfuss trägt lange. Beide Zahlen sind
 * aus der Vorlage übernommen und dort nicht hergeleitet.
 */
const ROLL_START = 0.03
const ROLL_SHAPE = 0.72

/** Punkte, deren Bahn die Dynamik braucht — Hebelarme, Auflage, Schwerpunkte. */
const TRACKED: NodeKey[] = [
  'hip',
  'knee',
  'ankle',
  'mtp',
  'ball',
  'heel',
  'toe',
  'shoulder',
  'elbow',
  'wrist',
]
const TRACKED_CENTRAL: NodeKey[] = ['pelvis', 'trunk', 'head', 'hands']

/**
 * Rechnet Kinematik und Kinetik für einen ganzen Zyklus.
 *
 * Rein: gleiche Eingaben, gleiches Ergebnis. Keine Szene, kein Zustand
 * ausserhalb der Argumente.
 */
export function computeDynamics(
  model: HumanoidModel,
  state: SimState,
  gait: GaitResult,
  ground: GroundResult,
): DynamicsResult {
  const n = gait.samples
  const dt = gait.cycleTime / n
  const skeleton = ground.skeleton

  /*
    DIE POSEN KOMMEN AUS DEM BODENKONTAKT, NICHT AUS DER ERZEUGUNG.

    Die Erzeugung liefert Gelenkwinkel und eine vorgegebene Beckenbahn; wo der
    Körper danach wirklich steht, entscheidet erst `ground.ts` — Höhenlage,
    Flugparabel und Standbein-Nachführung. Wer hier die vorgegebene Bahn nimmt,
    rechnet die Momente eines Roboters aus, der neben dem Boden läuft.
  */
  const poses = ground.poses

  /* --- Bahnen abtasten und filtern --------------------------------------- */
  const tracks: Record<string, Vec3[]> = {}
  for (const key of TRACKED) {
    for (const side of SIDES) {
      tracks[trackKey(key, side)] = poses.map((pose) => nodePoint(skeleton, pose, key, side))
    }
  }
  for (const key of TRACKED_CENTRAL) {
    tracks[trackKey(key, null)] = poses.map((pose) => nodePoint(skeleton, pose, key))
  }
  for (const path of Object.values(tracks)) smoothPath(path, FILTER.point)

  /* --- Segmentbahnen ---------------------------------------------------- */
  /*
    Die Eigenmasse ist die SUMME der Einzelmassen, nie eine eigene Zahl
    (`state.body` ist der Abgleichswert, keine Rechengrösse). Die Zuladung
    dagegen ist eine Eingabe und steht im Zustand.
  */
  const total = totalMass(model, state)
  const specs: SegmentSpec[] = segmentSpecs(state)

  const segments: SegmentTrack[] = specs.map((spec) => {
    const side = spec.side
    const mass = segmentMass(model, state, spec.key)
    const com = model.masses[spec.key].com

    const proximal = tracks[trackKey(spec.proximal, side)]
    const distal = tracks[trackKey(spec.distal, side)]
    const centre =
      spec.proximal === spec.distal
        ? proximal.map((p) => p)
        : proximal.map((p, i) => lerpVec(p, distal[i], com))

    const frame = poses.map((pose) => nodeFrame(pose, spec.frame, side ?? 'R'))
    smoothFrames(frame, FILTER.frame)

    const acceleration = secondDerivative(centre, dt)
    const omega: Vec3[] = []
    for (let i = 0; i < n; i++) {
      omega.push(angularVelocity(frame[(i - 1 + n) % n], frame[(i + 1) % n], 2 * dt))
    }
    const alpha = firstDerivative(omega, dt)

    return {
      key: spec.key,
      side,
      name: spec.name,
      mass,
      length: segmentLength(model, spec.key),
      centre,
      acceleration,
      omega,
      alpha,
      frame,
    }
  })

  /* --- Körperschwerpunkt ------------------------------------------------- */
  const com: Vec3[] = []
  for (let i = 0; i < n; i++) {
    let c = vec(0, 0, 0)
    let m = 0
    for (const seg of segments) {
      c = add(c, scale(seg.centre[i], seg.mass))
      m += seg.mass
    }
    com.push(scale(c, 1 / m))
  }

  /* Auf rund 3 Hz filtern. Ohne diese Glättung erzeugt der Fersenaufsatz einen
     Knick, an dem die Bodenreaktionskraft explodiert. */
  smoothPath(com, FILTER.com)
  const comAcceleration = secondDerivative(com, dt)

  /* --- Kraftangriffszentrum aus dem Momentengleichgewicht ---------------- */
  const zmp: Vec3[] = []
  for (let i = 0; i < n; i++) {
    const av = comAcceleration[i].y + G
    if (av > 0.4) {
      zmp.push(
        vec(
          com[i].x - (com[i].y * comAcceleration[i].x) / av,
          0,
          com[i].z - (com[i].y * comAcceleration[i].z) / av,
        ),
      )
    } else {
      zmp.push(vec(com[i].x, 0, com[i].z))
    }
  }

  /* --- Druckmittelpunkt je Fuss ------------------------------------------ */
  const cop: Record<Side, Vec3[]> = { R: [], L: [] }
  const fromZmp = state.features.zmp
  const stancePart = Math.max(0.05, stanceFraction(state))
  for (let i = 0; i < n; i++) {
    for (const side of SIDES) {
      /* Aus den GEFILTERTEN Bahnen, wie alles andere auch. */
      const cHeel = tracks[trackKey('heel', side)][i]
      const cBall = tracks[trackKey('ball', side)][i]
      const cToe = tracks[trackKey('toe', side)][i]
      const heel = vec(cHeel.x, 0, cHeel.z)
      /* Der Druckmittelpunkt läuft bis in den Vorfuss, nicht bis zur
         Zehenspitze — dort ist keine tragende Fläche mehr. */
      const front = vec(
        cBall.x + (cToe.x - cBall.x) * FOREFOOT,
        0,
        cBall.z + (cToe.z - cBall.z) * FOREFOOT,
      )
      const axis = sub(front, heel)
      const len2 = dot(axis, axis) || 1

      if (!fromZmp) {
        /*
          FEST VORGEGEBENE ABROLLKURVE — der Schalter `zmp` aus.

          Der Druckmittelpunkt läuft über die Standphase von der Ferse in den
          Vorfuss, nach einer Kurve, die niemand hergeleitet hat: sie sieht
          richtig aus. Genau das ist ihr Wert hier — sie zeigt, was man verliert,
          wenn man ihn NICHT aus dem Momentengleichgewicht nimmt.

          Was fehlt: die Kurve weiss nichts von der Beschleunigung des
          Schwerpunkts. Bremst der Körper, wandert der wirkliche Angriffspunkt
          nach hinten; hier bleibt er auf seiner Bahn. Die Momente am
          Sprunggelenk folgen dann einer Vorgabe und nicht der Bewegung.
        */
        const phase = side === 'R' || inPhase(state.gait) ? i / n : (i / n + 0.5) % 1
        const q = clamp(phase / stancePart, 0, 1)
        const u = clamp(ROLL_START + (1 - ROLL_START) * Math.pow(q, ROLL_SHAPE), 0, 1)
        const point = add(heel, scale(axis, u))
        cop[side].push(vec(point.x, 0, point.z))
        continue
      }

      const rel = sub(zmp[i], heel)
      const u = clamp(dot(rel, axis) / len2, 0, 1)
      let point = add(heel, scale(axis, u))

      /* Seitlich auf die Fussbreite begrenzen. */
      const lateral = sub(zmp[i], point)
      const normal = vec(-axis.z, 0, axis.x)
      const nl = length(normal) || 1
      const unit = scale(normal, 1 / nl)
      point = add(point, scale(unit, clamp(dot(lateral, unit), -HALF_FOOT_WIDTH, HALF_FOOT_WIDTH)))
      cop[side].push(vec(point.x, 0, point.z))
    }
  }

  /* Der ZMP springt an den Rändern der Stützfläche — leicht glätten. */
  for (const side of SIDES) smoothPath(cop[side], COP_SMOOTHING)

  /* --- Lastverteilung und Bodenreaktionskraft ---------------------------- */
  const load: Record<Side, number[]> = { R: [], L: [] }
  const grf: Record<Side, Vec3[]> = { R: [], L: [] }
  /* Im Stand tragen beide Füsse dauernd; sonst gibt der Gang den
     Standphasenanteil vor — beim gerechneten die Aufgabe, beim aufgezeichneten
     das Gangbild. */
  const duty = stanceFraction(state)
  const running = isRunning(state)
  const together = inPhase(state.gait)

  for (let i = 0; i < n; i++) {
    const phase = i / n
    const force = scale(
      vec(comAcceleration[i].x, comAcceleration[i].y + G, comAcceleration[i].z),
      total,
    )
    /* Gleichphasige Bewegungen tragen mit BEIDEN Beinen zugleich. Ein Versatz
       von einer halben Periode wäre hier kein Gang, sondern ein Fehler: der
       Hackensprung stünde damit dauernd auf einem Bein. */
    const w: Record<Side, number> = {
      R: stanceWeight(phase, duty, state.generated, running),
      L: stanceWeight(together ? phase : (phase + 0.5) % 1, duty, state.generated, running),
    }
    const sum = w.R + w.L
    /* Beide Füsse tragen zusammen IMMER die volle Schwerpunktkraft. */
    const inv = sum > 0.04 ? 1 / sum : 0
    for (const side of SIDES) {
      load[side].push(w[side] * inv)
      grf[side].push(scale(force, w[side] * inv))
    }
  }

  let stanceSamples = 0
  for (let i = 0; i < n; i++) if (load.R[i] > 0.02) stanceSamples++

  /* --- Newton-Euler vom Fuss aufwärts ------------------------------------ */
  const moments: Record<string, Vec3[]> = {}
  /*
    Die Hebelarme kommen aus den GEFILTERTEN Bahnen — wie die Beschleunigungen
    auch. Ein Hebelarm aus der ungefilterten Pose, multipliziert mit einer Kraft
    aus der gefilterten Schwerpunktbahn, mischt zwei verschiedene Bewegungen und
    lässt die Momentenbilanz um Prozente danebenliegen.
  */
  const chain: { seg: SegmentKey; joint: string; node: NodeKey }[] = [
    { seg: 'foot', joint: 'ankle', node: 'ankle' },
    { seg: 'shank', joint: 'knee', node: 'knee' },
    { seg: 'thigh', joint: 'hip', node: 'hip' },
  ]

  /*
    Der Arm hat ein FREIES Ende: am Handgelenk greift nichts an, solange der
    Roboter nichts trägt. Die Kette ist dieselbe, nur beginnt sie mit Kraft und
    Moment null statt mit der Bodenreaktion. Ohne diese zweite Kette gäbe es für
    Schulter und Ellbogen keine Momente — und damit keinen Antrieb in der Liste.
  */
  const armChain: { seg: SegmentKey; joint: string; node: NodeKey }[] = [
    { seg: 'forearm', joint: 'elbow', node: 'elbow' },
    { seg: 'upperArm', joint: 'shoulder', node: 'shoulder' },
  ]

  for (const side of SIDES) {
    for (const link of [...chain, ...armChain]) moments[jointKey(link.joint, side)] = []

    for (let i = 0; i < n; i++) {
      /*
        Die getragene Last hängt an den Händen. Sie wird je zur Hälfte auf die
        beiden Handgelenke gelegt: die Kiste wird beidhändig geführt, und wo sie
        sitzt, sagt die Haltung — es braucht keine eigene Zahl dafür.
      */
      const payload = carrying(state) ? segments.find((s) => s.key === 'payload') : undefined
      const handForce = payload
        ? scale(add(payload.acceleration[i], vec(0, G, 0)), -0.5 * payload.mass)
        : vec(0, 0, 0)

      const starts: {
        links: { seg: SegmentKey; joint: string; node: NodeKey }[]
        force: Vec3
        moment: Vec3
        point: Vec3
      }[] = [
        { links: chain, force: grf[side][i], moment: vec(0, 0, 0), point: cop[side][i] },
        {
          links: armChain,
          force: handForce,
          moment: vec(0, 0, 0),
          point: tracks[trackKey('wrist', side)][i],
        },
      ]

      for (const start of starts) {
        let forceDistal = start.force
        let momentDistal = start.moment
        let pointDistal = start.point

        for (const link of start.links) {
          const track = segments.find((s) => s.key === link.seg && s.side === side)!
          const item = model.masses[link.seg]
          const m = track.mass
          const centre = track.centre[i]

          /* Trägheit um die Querachsen aus dem Trägheitsradius, um die Längsachse
             mit 30 % davon — aus der Vorlage übernommen. Ein Glied ist keine
             Kugel: um die eigene Achse dreht es sich viel leichter. */
          const ix = m * Math.pow(item.gyration * track.length, 2)
          const iy = 0.3 * ix
          const inertiaWorld = rotateInertia(track.frame[i], ix, iy)

          /* Kraftbilanz: was am proximalen Gelenk anliegen muss. */
          const forceProximal = sub(
            add(scale(track.acceleration[i], m), vec(0, m * G, 0)),
            forceDistal,
          )

          /* Momentenbilanz um den Segmentschwerpunkt. */
          const ia = apply(inertiaWorld, track.alpha[i])
          const iw = apply(inertiaWorld, track.omega[i])
          let momentProximal = add(ia, cross(track.omega[i], iw))

          const rProximal = tracks[trackKey(link.node, side)][i]
          momentProximal = sub(momentProximal, cross(sub(rProximal, centre), forceProximal))
          momentProximal = sub(momentProximal, cross(sub(pointDistal, centre), forceDistal))
          momentProximal = sub(momentProximal, momentDistal)

          moments[jointKey(link.joint, side)].push(momentProximal)

          forceDistal = scale(forceProximal, -1)
          momentDistal = scale(momentProximal, -1)
          pointDistal = rProximal
        }
      }
    }
  }

  return {
    samples: n,
    cycleTime: gait.cycleTime,
    skeleton,
    poses,
    segments,
    tracks,
    mass: total,
    com,
    comAcceleration,
    zmp,
    grf,
    cop,
    load,
    moments,
    stancePercent: (stanceSamples / n) * 100,
  }
}

/**
 * Trägheitstensor eines Gliedes im Weltsystem.
 *
 * Lokal ist er diagonal: `iy` um die Längsachse (y), `ix` um die beiden
 * Querachsen. `R · I · Rᵀ` dreht ihn ins Weltsystem.
 */
function rotateInertia(frame: Mat3, ix: number, iy: number): Mat3 {
  const diag = Float64Array.from([ix, 0, 0, 0, iy, 0, 0, 0, ix])
  const t = transpose(frame)
  const out = new Float64Array(9)
  // frame · diag · frameᵀ
  for (let c = 0; c < 3; c++) {
    for (let r = 0; r < 3; r++) {
      let sum = 0
      for (let k = 0; k < 3; k++) {
        // (frame · diag)[r][k] = frame[r][k] · diag[k][k]
        const fd = frame[k * 3 + r] * diag[k * 3 + k]
        sum += fd * t[c * 3 + k]
      }
      out[c * 3 + r] = sum
    }
  }
  return out
}

/* -------------------------------------------------------------------------
   Momente in den Gelenkebenen
   ------------------------------------------------------------------------- */

/**
 * Vorzeichen je Gelenk.
 *
 * Alle negativ: die aufgezeichneten Momentenkurven zählen das INNERE Moment
 * (was der Muskel beziehungsweise der Antrieb aufbringt), die
 * Newton-Euler-Kette liefert das äussere. Wer das Vorzeichen vergisst, bekommt
 * eine Kurve, die richtig aussieht und in jeder Phase das Gegenteil behauptet.
 */
export const MOMENT_SIGN: Record<string, number> = {
  ankle: -1,
  knee: -1,
  hip: -1,
  shoulder: -1,
  elbow: -1,
}

/** Die Gelenke, für die die Kette ein Moment liefert. */
export const MOMENT_JOINTS = ['ankle', 'knee', 'hip', 'shoulder', 'elbow'] as const

export type MomentJoint = (typeof MOMENT_JOINTS)[number]

export interface PlaneMoments {
  sag: Float32Array
  fro: Float32Array
  tra: Float32Array
  /** Gelenkleistung, W. */
  power: Float32Array
  /** Aufsummierte Energie über den Zyklus, J. */
  energy: Float32Array
}

/**
 * Zerlegt das Weltmoment in die drei Gelenkebenen.
 *
 * Projiziert wird auf die Achsen des PROXIMALEN Segments — das Moment am Knie
 * wird im Oberschenkelsystem gemessen, nicht im Weltsystem. Ein Moment, das man
 * im Weltsystem ablesen würde, hätte in jeder Phase eine andere Bedeutung.
 */
export function planeMoments(result: DynamicsResult, joint: MomentJoint, side: Side): PlaneMoments {
  const n = result.samples
  const dt = result.cycleTime / n
  const sign = MOMENT_SIGN[joint]

  const proximalKey: Record<MomentJoint, string> = {
    ankle: `shank${side}`,
    knee: `thigh${side}`,
    hip: 'trunk',
    shoulder: 'trunk',
    elbow: `upperArm${side}`,
  }
  const distalKey: Record<MomentJoint, string> = {
    ankle: `foot${side}`,
    knee: `shank${side}`,
    hip: `thigh${side}`,
    shoulder: `upperArm${side}`,
    elbow: `forearm${side}`,
  }

  const proximal = result.segments.find((s) => s.name === proximalKey[joint])!
  const distal = result.segments.find((s) => s.name === distalKey[joint])!
  const moments = result.moments[jointKey(joint, side)]

  const sag = new Float32Array(n + 1)
  const fro = new Float32Array(n + 1)
  const tra = new Float32Array(n + 1)
  const power = new Float32Array(n + 1)
  const energy = new Float32Array(n + 1)

  for (let i = 0; i < n; i++) {
    const m = moments[i]
    const f = proximal.frame[i]
    /* Spalten der Matrix sind die Achsen: x = frontal, y = transversal,
       z = sagittal. */
    fro[i] = sign * (m.x * f[0] + m.y * f[1] + m.z * f[2])
    tra[i] = sign * (m.x * f[3] + m.y * f[4] + m.z * f[5])
    sag[i] = sign * (m.x * f[6] + m.y * f[7] + m.z * f[8])

    /* Leistung ist Moment mal RELATIVE Winkelgeschwindigkeit der beiden
       Glieder — nicht mal der absoluten. Ein Bein, das im Raum schwingt,
       während das Gelenk stillsteht, verrichtet dort keine Arbeit. */
    const relative = sub(distal.omega[i], proximal.omega[i])
    power[i] = dot(m, relative)
  }

  sag[n] = sag[0]
  fro[n] = fro[0]
  tra[n] = tra[0]
  power[n] = power[0]

  let accumulated = 0
  for (let i = 0; i < n; i++) {
    accumulated += power[i] * dt
    energy[i] = accumulated
  }
  energy[n] = energy[0]

  return { sag, fro, tra, power, energy }
}

/* -------------------------------------------------------------------------
   Prüfgrössen
   ------------------------------------------------------------------------- */

export interface Balance {
  /** Senkrechter Kraftimpuls über den Zyklus, Ns. */
  verticalImpulse: number
  /** Was er sein müsste: Gewicht mal Zyklusdauer. */
  verticalExpected: number
  /** Waagerechter Kraftimpuls, Ns — muss über den Zyklus null sein. */
  horizontalImpulse: number
  /** Relative Abweichung des senkrechten Impulses. */
  verticalError: number
}

/**
 * Die Impulsbilanz — die Prüfgrösse, die das Modell selbst nennt.
 *
 * Über einen vollen Zyklus muss der senkrechte Bodenkraftimpuls gleich Gewicht
 * mal Zyklusdauer sein: der Körper kommt dorthin zurück, wo er war. Der
 * waagerechte Impuls muss null sein, sonst würde der Körper beschleunigen.
 *
 * Stimmt das nicht, ist irgendwo Kraft verlorengegangen — und alle Momente
 * darunter sind falsch, ohne dass man es ihnen ansieht.
 */
export function impulseBalance(result: DynamicsResult): Balance {
  const dt = result.cycleTime / result.samples
  let vertical = 0
  let horizontal = 0

  for (let i = 0; i < result.samples; i++) {
    for (const side of SIDES) {
      vertical += result.grf[side][i].y * dt
      horizontal += result.grf[side][i].x * dt
    }
  }

  const expected = result.mass * G * result.cycleTime
  return {
    verticalImpulse: vertical,
    verticalExpected: expected,
    horizontalImpulse: horizontal,
    verticalError: (vertical - expected) / expected,
  }
}

/** Nur zur Gegenprobe verfügbar gemacht. */
export const rotateInertiaTensor = rotateInertia
